import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import { InvoiceTypeEnum } from '@nxsys/constants/AppEnums';
import { useCreateCreditNote } from '@nxsys/hooks/api/invoice/useInvoiceApi';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { CreateCreditNoteParams } from '@nxsys/types/api';
import { makeCode } from '@nxsys/utils';
import { Space } from 'antd';
import moment from 'moment';
import { useCallback, useMemo, useRef } from 'react';
import InvoiceForm, {
  InvoiceFormRef,
  InvoiceFormValues,
} from '../components/InvoiceForm';
import { showMessage } from '@nxsys/utils/message';

const CreateCreditNote = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const { run, loading } = useCreateCreditNote();
  const crnNumberDefault = useMemo(() => makeCode('SON', 10), []);

  const formRef = useRef<InvoiceFormRef>(null);

  const handleGoBack = () => navigate('/invoices/credit-notes');

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <div className="flex items-center gap-x-3">
          <AppPageTitle
            separator=""
            prefix={messages['invoices.addNewCreditNote']}
            title=""
          />
        </div>
      </div>
    );
  }, [messages]);

  const renderRightContent = useCallback(() => {
    return (
      <div className="flex items-center justify-between">
        <Space>
          <AppButton
            variant="outlined"
            type="primary"
            name={messages['common.cancel']}
            onClick={handleGoBack}
          />
          <AppButton
            type="primary"
            name={messages['common.save']}
            onClick={() => {
              formRef.current?.submit();
            }}
            loading={loading}
            disabled={loading}
          />
        </Space>
      </div>
    );
  }, [messages, formRef, loading]);

  const handleSubmit = (values: InvoiceFormValues) => {
    const params: CreateCreditNoteParams = {
      agencyRef: values.agencyRef?.value ?? '',
      companyRef: values.companyRef?.value ?? '',
      creditDate: moment(values.creditDate).toISOString(),
      creditNo: `CRN-${crnNumberDefault}`,
      invoiceNo: `INV-${crnNumberDefault}`,
      reference: values.reference ?? '',
      rates:
        values.rates?.map((rate) => ({
          ...rate,
          rateCode: rate.rateCode?.value ?? '',
          vatDetails: rate.vatDetails?.value ?? '',
          includeInCisCalculation: true,
        })) ?? [],
    };
    run(params)
      .then(() => {
        showMessage({
          type: 'success',
          content: messages['invoices.createCreditNoteSuccess'],
        });
        handleGoBack();
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={loading}
    >
      <InvoiceForm
        formRef={formRef}
        onSubmit={handleSubmit}
        invoiceType={InvoiceTypeEnum.CREDIT_NOTE}
      />
    </AppsContainer>
  );
};

export default CreateCreditNote;
